import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { defaultStartFor, isSingleDayMode, todayKey } from "../../utils/defaultRange";

import { AvgActiveChart } from "../../components/charts3";
import AvgActiveEpgChart from "../../components/charts3/AvgActiveEpgChart";
import MultiLineAvgChart from "../../components/charts3/MultiLineAvgChart";
import { ChartControls } from "../../components/Channel";
import TimezonePicker from "../../components/Channel/TimezonePicker";
import {
  browserTimezone,
  epgBlocksInTimezone,
  nowInTimezone,
  shiftMinutesFor,
} from "../../utils/avgActiveTimezone";

import { useDaysSorted } from "../../hooks/useDaysSorted";

import { useAvgActiveSeries } from "../../hooks/useAvgActiveSeries";
import { useEpgData } from "../../hooks/useEpgData";

import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";
import { programEndClock } from "../../utils/epgTime";


const PROGRAM_COLORS = [
  '#E6194B','#3CB44B','#FFE119','#4363D8','#F58231',
  '#911EB4','#46F0F0','#F032E6','#BCF60C','#FABEBE',
  '#008080','#E6BEFF','#9A6324','#FFFAC8','#800000'
];

function getProgramColor(name,map){
  if(!map[name]){
    const idx = Object.keys(map).length % PROGRAM_COLORS.length;
    map[name] = PROGRAM_COLORS[idx];
  }
  return map[name];
}

export default function ChannelAvgActive({
  channelId,
  channelInfo,
  rawVideo,
  ttvRawVideo,
  refreshing,
  refreshKey,
  refreshEnabled,
  setRefreshEnabled,
  refreshIntervalMin,
  setRefreshIntervalMin,
  refreshCountdown,
  manualRefresh
}){

  const storedUser = JSON.parse(localStorage.getItem("adm_panel_user") || "{}");

  const defaultMode = storedUser.chartdefault || "minute";
  const isDark = !!storedUser.darkmode;
  const TTVAllowance = !!storedUser.ttvallowance;

  const [mode,setMode] = useState(defaultMode);
  const [selectedProgram,setSelectedProgram] = useState(null);
  const [timezone,setTimezone] = useState(browserTimezone);

  const [startTime,setStartTime] = useState("00:00");
  const [endTime,setEndTime] = useState("23:59");

  // Independent date state for this chart
  const [endDateInput, setEndDateInput] = useState(todayKey);
  const [startDateInput, setStartDateInput] = useState(() =>
    defaultStartFor(defaultMode, todayKey())
  );

  // The single-day picker reports only a start, so the end follows it there.
  const handleStartDateChange = (val) => {
    setStartDateInput(val);
    if (isSingleDayMode(mode)) setEndDateInput(val);
  };


  const sortedKeys = useDaysSorted(rawVideo);

  const series = useAvgActiveSeries({
    rawVideo,
    sortedVideoKeys: sortedKeys,
    startDateInput,
    endDateInput,
    startTime,
    endTime,
    mode,
    timezone,
    sourceOffset: channelInfo?.tzOffset ?? 3,
  });

  const [ttvScale, setTtvScale] = useState("linear");

  const ttvChartSeries = useMemo(() => {
    if (mode !== "ttv" || !startDateInput) return [];

    const toMinute = (t) => {
      const [h, m] = (t || "00:00").split(":").map(Number);
      return h * 60 + m;
    };

    let startMin = toMinute(startTime);
    let endMin = toMinute(endTime);
    if (!Number.isFinite(startMin)) startMin = 0;
    if (!Number.isFinite(endMin)) endMin = 1439;
    if (endMin < startMin) [startMin, endMin] = [endMin, startMin];

    const dateKey = startDateInput.split("-").reverse().join("-"); // YYYY-MM-DD -> DD-MM-YYYY

    const sliceToData = (source) => {
      const arr = source?.[dateKey] || [];
      const data = [];
      for (let i = startMin; i <= endMin; i++) {
        const hh = String(Math.floor(i / 60)).padStart(2, "0");
        const mm = String(i % 60).padStart(2, "0");
        data.push({ x: `${hh}:${mm}`, active: Math.round((Number(arr[i]) || 0) / 2) });
      }
      return data;
    };

    const result = [];
    if (rawVideo) {
      result.push({
        name: channelInfo?.name || channelInfo?.n || "This channel",
        data: sliceToData(rawVideo),
      });
    }
    if (ttvRawVideo) {
      result.push({ name: "TMTV", data: sliceToData(ttvRawVideo) });
    }
    return result;
  }, [mode, rawVideo, ttvRawVideo, startDateInput, startTime, endTime, channelInfo]);

  const { logPageChange,logTimeframeChange } = useUserJourneyLogger({
    channelId,
    channelName: channelInfo?.name || channelInfo?.n || "unknown",
  });

  useEffect(()=>{
    logPageChange({
      chartType:"avgactivetv",
    });
  },[logPageChange]);

  const handleModeChange=(newMode)=>{
    setMode(newMode);
    setStartDateInput(defaultStartFor(newMode, endDateInput));
    logTimeframeChange({
      chartType:"avgactivetv",
      mode:newMode,
    });
  };

  useEffect(()=>{
    if(mode==="minute" || mode==="ttv"){
      setStartTime("00:00");
      setEndTime("23:59");
    }
  },[mode]);

  const channelTz = channelInfo?.tzOffset ?? 3;

  const shiftMin = useMemo(
    () => shiftMinutesFor(timezone, startDateInput, channelTz),
    [timezone, startDateInput, channelTz]
  );

  // A shifted day reaches into the neighbouring Turkey days, so their programmes are needed too.
  const epgRange = useMemo(() => {
    if (!shiftMin || !startDateInput) return {};
    const d = dayjs(startDateInput);
    return {
      startDateRange: d.subtract(1, "day").format("YYYY-MM-DD"),
      endDateRange: d.add(1, "day").format("YYYY-MM-DD"),
    };
  }, [shiftMin, startDateInput]);

  const { epgBlocks, loading: epgLoading } = useEpgData(channelId, startDateInput, epgRange);

  const handleTimezoneChange = (tz) => {
    setTimezone(tz);
    setSelectedProgram(null);
    setStartTime("00:00");
    setEndTime("23:59");
  };

  const epgForSelectedDate = useMemo(()=>{
    if((mode!=="epg" && mode!=="minute") || !startDateInput) return [];

    if(shiftMin){
      return epgBlocksInTimezone(epgBlocks,startDateInput,shiftMin);
    }

    return epgBlocks
      .filter(b=>b.keyDate===startDateInput)
      .sort((a,b)=>a.startMinute-b.startMinute);

  },[mode,startDateInput,epgBlocks,shiftMin]);

  const minuteSeries = useMemo(()=>{

    if(mode!=="epg" || !series?.length) return [];

    return series.map(p=>({
      minute:p.index ?? 0,
      value:Number(p.value)||0
    }));

  },[mode,series]);

  // On today's chart the array runs to midnight but only the minutes that have
  // happened carry a reading; the rest are zeroes. Averaging them in halved a
  // programme that was still on air.
  const lastRecordedMinute = useMemo(() => {
    const now = nowInTimezone(timezone);
    return startDateInput === now.key ? now.minute : 1439;
  }, [startDateInput, timezone]);

  const programWindows = useMemo(()=>{

    if(!epgForSelectedDate.length) return [];

    const colorMap = {};

    let programs = epgForSelectedDate;

    if(selectedProgram){
      programs = epgForSelectedDate.filter(p=>p.id===selectedProgram.id);
    }

    return programs.map(p=>({
      name:p.programName,
      startMinute:p.startMinute,
      endMinute:p.endMinute,
      color:getProgramColor(p.programName,colorMap)
    }));

  },[epgForSelectedDate,selectedProgram]);

  return (
    <>
      <ChartControls
        channelId={channelId}
        mode={mode}
        onModeChange={handleModeChange}
        startDateInput={startDateInput}
        endDateInput={endDateInput}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={setEndDateInput}
        startTime={startTime}
        endTime={endTime}
        onStartTimeChange={setStartTime}
        onEndTimeChange={setEndTime}
        allowedModes={
          TTVAllowance
            ? ["minute","hour","day","week","month","epg","ttv"]
            : ["minute","hour","day","week","month","epg"]
        }
        extraControls={(mode === "minute" || mode === "epg") ? (
          <TimezonePicker value={timezone} onChange={handleTimezoneChange} isDark={isDark} channelOffset={channelTz} />
        ) : mode === "ttv" && (
          <div className="chart-mode-buttons">
            <button
              className={ttvScale === "linear" ? "active" : ""}
              onClick={() => setTtvScale("linear")}
            >
              LINEAR
            </button>
            <button
              className={ttvScale === "log" ? "active" : ""}
              onClick={() => setTtvScale("log")}
            >
              LOG
            </button>
          </div>
        )}
        refreshEnabled={refreshEnabled}
        onRefreshToggle={setRefreshEnabled}
        refreshIntervalMin={refreshIntervalMin}
        onRefreshIntervalChange={setRefreshIntervalMin}
        countdown={refreshCountdown}
        onManualRefresh={manualRefresh}
      />

      {(mode === "epg" || mode === "minute") && !epgLoading && (

        <div className={`epg-drawer ${isDark?"dark":"light"}`}>

          <label style={{fontWeight:600,fontSize:14}}>
            Select Program:
          </label>

          <select
            value={selectedProgram?.id || ""}
            onChange={(e)=>{

              const prog = epgForSelectedDate.find(p=>p.id===e.target.value);

              setSelectedProgram(prog||null);

              if(prog){

                const startH = String(Math.floor(prog.startMinute/60)).padStart(2,"0");
                const startM = String(prog.startMinute%60).padStart(2,"0");


                setStartTime(`${startH}:${startM}`);
                setEndTime(programEndClock(prog));

              }else{
                setStartTime("00:00");
                setEndTime("23:59");
              }

            }}
            style={{
              flex:1,
              maxWidth:600,
              padding:'8px 12px',
              fontSize:14,
              borderRadius:8,
              cursor:'pointer'
            }}
          >

            <option value="">
              00:00 - 23:59 | Full Day
            </option>

            {epgForSelectedDate.map(prog=>{

              const startH = String(Math.floor(prog.startMinute/60)).padStart(2,"0");
              const startM = String(prog.startMinute%60).padStart(2,"0");

              const endH = String(Math.floor(prog.endMinute/60)).padStart(2,"0");
              const endM = String(prog.endMinute%60).padStart(2,"0");

              return(
                <option key={prog.id} value={prog.id}>
                  {startH}:{startM} - {endH}:{endM} | {prog.programName}
                </option>
              );

            })}

          </select>

        </div>

      )}

      {mode==="epg" && (
        <AvgActiveEpgChart
          minuteSeries={minuteSeries}
          programSeries={programWindows}
          lastMinute={lastRecordedMinute}
        />
      )}

      {mode==="ttv" && (
        <MultiLineAvgChart
          key={refreshKey}
          title="Average Active TV / TMTV"
          series={ttvChartSeries}
          colors={["#1976d2", "#eb4132"]}
          yAxisScale={ttvScale}
        />
      )}

      {mode!=="epg" && mode!=="ttv" && (
        <AvgActiveChart
          key={refreshKey}
          series={series}
          mode={mode}
        />
      )}
    </>
  );
}